/**
 * ABTestPanel
 * -----------
 * The ABTestTable plus its "set winner" / "start over" confirmation modals
 * and the network calls that back them. Extracted out of ABTestDashboard.tsx
 * so the same table + actions can be reused inside a single-bundle context
 * (see StopABTestModal.tsx), not just the full dashboard.
 */
import React, { useMemo, useState } from "react";
import ABTestTable from "./ABTestTable";
import { ABTestRow, ABTestStatus } from "../types";
import CustomModal from "../../../components/CustomModal/CustomModal";
import { endPoints, postData } from "../../../utilities/networkCalls";
import { BlockStack, Icon, Text } from "@shopify/polaris";
import { CheckIcon } from "@shopify/polaris-icons";
import useBundleMixpanelHook from "../../../hooks/useBundleMixpanelHook";

interface ABTestPanelProps {
    rows: ABTestRow[];
    loading: boolean;
    status: ABTestStatus;
    selectedTabIndex: number;
    onTabChange: (index: number) => void;
    selectedBundleTypes: string[];
    onBundleTypesChange: (values: string[]) => void;
    /** Hides the Running/Draft/Completed tabs + category filter bar (e.g. when embedded for a single, known bundle). */
    hideFilters?: boolean;
    /**
     * True when this panel is embedded inside a bundle editor's own form
     * (e.g. StopABTestModal), as opposed to the standalone ABTestDashboard.
     * When true, "Set winner" calls `onWinnerSet` with the freshly-merged
     * bundle settings instead of redirecting the dashboard's tabs — the
     * caller resets its own form with that data and clears its dirty state.
     */
    isBundleFormContext?: boolean;
    onWinnerSet?: (bundle: any) => void;
}

const ChecklistItem = ({ children }: { children: React.ReactNode }) => (
    <div style={{ display: "flex", flexDirection: "row", alignItems: "flex-start", gap: 8 }}>
        <span style={{ flex: "0 0 auto" }}>
            <Icon source={CheckIcon} tone="success" />
        </span>
        <Text as="span">{children}</Text>
    </div>
);

const ABTestPanel: React.FC<ABTestPanelProps> = ({
    rows,
    loading,
    status,
    selectedTabIndex,
    onTabChange,
    selectedBundleTypes,
    onBundleTypesChange,
    hideFilters = false,
    isBundleFormContext = false,
    onWinnerSet,
}) => {
    const [winnerTarget, setWinnerTarget] = useState<{ row: ABTestRow; variantId: string } | null>(null);
    const [startOverTarget, setStartOverTarget] = useState<ABTestRow | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const { trackCtaClick } = useBundleMixpanelHook();

    const confirmSetWinner = async () => {
        if (!winnerTarget) return;
        trackCtaClick({
            cta: "Set Winner",
            ctaType: "Click",
            bundleType: winnerTarget.row?.bundle_type,
        });
        setActionLoading(true);
        const res = await postData({
            url: endPoints.markAbTestWinner,
            payload: {
                form_id: winnerTarget.row.form_id,
                exp_id: winnerTarget.row.exp_id,
                winner_variant_id: winnerTarget.variantId,
            },
        });
        setActionLoading(false);
        setWinnerTarget(null);
        if (res?.status === 200 || res?.status === 201) {
            if (isBundleFormContext) {
                onWinnerSet?.(res?.data?.data?.bundle);
            } else {
                onTabChange(2);
            }
        } else {
            shopify.toast.show(res?.data?.message || "Failed to set winner. Please try again.", {
                duration: 4000,
            });
        }
    };

    const confirmStartOver = async () => {
        if (!startOverTarget) return;
        trackCtaClick({
            cta: "Start Over",
            ctaType: "Click",
            bundleType: startOverTarget?.bundle_type,
        });
        setActionLoading(true);
        const res = await postData({
            url: endPoints.startOverAbTest,
            payload: { form_id: startOverTarget.form_id, exp_id: startOverTarget.exp_id },
        });
        setActionLoading(false);
        setStartOverTarget(null);
        if (res?.status === 200 || res?.status === 201) {
            onTabChange(res?.data?.data?.status === true ? 0 : 1);
        } else {
            shopify.toast.show(res?.data?.message || "Failed to start over. Please try again.", {
                duration: 4000,
            });
        }
    };

    const winnerVariantLabel = useMemo(
        () => winnerTarget?.row.variants.find((v) => v.id === winnerTarget.variantId)?.label,
        [winnerTarget],
    );

    return (
        <>
            <ABTestTable
                rows={rows}
                loading={loading}
                status={status}
                selectedTabIndex={selectedTabIndex}
                onTabChange={onTabChange}
                selectedBundleTypes={selectedBundleTypes}
                onBundleTypesChange={onBundleTypesChange}
                onSetWinner={(row, variantId) => setWinnerTarget({ row, variantId })}
                onStartOver={setStartOverTarget}
                hideFilters={hideFilters}
            />

            <CustomModal
                title={`Set ${winnerVariantLabel} as Winner?`}
                open={!!winnerTarget}
                size="small"
                onClose={() => setWinnerTarget(null)}
                children={
                    <BlockStack gap="300">
                        <Text as="p" fontWeight="medium">
                            This will:
                        </Text>
                        <BlockStack gap="200">
                            <ChecklistItem>Make {winnerVariantLabel} the active offer</ChecklistItem>
                            <ChecklistItem>End the current A/B test & stop traffic splitting</ChecklistItem>
                            <ChecklistItem>Mark the test as Completed</ChecklistItem>
                        </BlockStack>
                    </BlockStack>
                }
                secondaryActions={[{ content: "Cancel", onAction: () => setWinnerTarget(null) }]}
                primaryAction={{
                    content: "Set winner",
                    loading: actionLoading,
                    onAction: confirmSetWinner,
                }}
            />

            <CustomModal
                title={`Start a new A/B test for "${startOverTarget?.title}"?`}
                open={!!startOverTarget}
                size="small"
                onClose={() => setStartOverTarget(null)}
                children={
                    <BlockStack gap="300">
                        <Text as="p" fontWeight="medium">
                            This will:
                        </Text>
                        <BlockStack gap="200">
                            <ChecklistItem>Reuse the same variants</ChecklistItem>
                            <ChecklistItem>Reset visitor and revenue stats</ChecklistItem>
                            <ChecklistItem>Start traffic splitting again</ChecklistItem>
                        </BlockStack>
                    </BlockStack>
                }
                secondaryActions={[{ content: "Cancel", onAction: () => setStartOverTarget(null) }]}
                primaryAction={{
                    content: "Start over",
                    loading: actionLoading,
                    onAction: confirmStartOver,
                }}
            />
        </>
    );
};

export default ABTestPanel;
